/**
 * GlobalFooter — Shared footer for landing pages and internal apps.
 *
 * Sections: optional newsletter form (with result-pattern submit feedback),
 * multi-column link grid, social media icons (lucide-react), and legal row.
 *
 * Newsletter submit callback follows the result pattern:
 *   { ok: true } | { ok: false; error: string }
 * Passing a void callback (or omitting the prop) defaults to success state.
 *
 * Colors resolve via `--primary` and semantic tokens for per-app theming.
 *
 * @package @engagexp/legendary-ui-primitives
 */

import * as React from 'react';
import { cn } from '../../../utils/cn';
import { Button } from '../../button';
import { Input } from '../../input';
import { Separator } from '../../separator';
import styles from './global-footer.module.css';

export interface FooterLinkItem {
  title: string;
  url: string;
}

export interface FooterColumn {
  title: string;
  links: FooterLinkItem[];
}

export interface FooterSocialLink {
  icon: React.ReactNode;
  href: string;
  /** Screen-reader label, e.g. "EngageXP on LinkedIn" */
  label: string;
}

export interface NewsletterConfig {
  heading: string;
  description: string;
  inputPlaceholder?: string;
  buttonLabel: string;
  /** Legal / terms copy rendered below the form. Pass a React node for rich content. */
  termsNode?: React.ReactNode;
}

type SubmitResult = { ok: true } | { ok: false; error: string };

/** Default newsletter config — renders when no `newsletter` prop is passed.
 *  Per OPORD-LAYOUT-FIX-01 §3.3, newsletter is always-on (opt-OUT). */
const DEFAULT_NEWSLETTER: NewsletterConfig = {
  heading: 'Stay in the loop',
  description: 'Get the latest on gaming, product updates, and EngageXP news.',
  inputPlaceholder: 'Enter your email',
  buttonLabel: 'Subscribe',
};

export interface GlobalFooterProps {
  logo: {
    src: string;
    alt?: string;
    href?: string;
  };
  /** Newsletter section config. Defaults to a generic EngageXP newsletter.
   *  Pass custom config to override heading/copy. Use `hideNewsletter` to suppress. */
  newsletter?: NewsletterConfig;
  /** Set to true to hide the newsletter section entirely. Default: false. */
  hideNewsletter?: boolean;
  /**
   * Called on newsletter submit. May return a SubmitResult for error/success handling.
   * Returning void or undefined is treated as success.
   */
  onNewsletterSubmit?: (email: string) => Promise<SubmitResult> | SubmitResult | void;
  columns?: FooterColumn[];
  socialLinks?: FooterSocialLink[];
  legalText?: string;
  legalLinks?: FooterLinkItem[];
  className?: string;
}

type SubmitState = 'idle' | 'pending' | 'success' | 'error';

/**
 * Wraps every occurrence of "EngageXP" in legalText with bold+primary styling.
 * Avoids passing pre-formatted JSX through the string prop.
 */
function formatLegalText(text: string): React.ReactNode {
  const parts = text.split('EngageXP');
  if (parts.length === 1) return text;
  return parts.reduce<React.ReactNode[]>((acc, part, i) => {
    if (i > 0) {
      acc.push(
        <strong key={i} className={styles.legalHighlight}>
          EngageXP
        </strong>,
      );
    }
    if (part) acc.push(part);
    return acc;
  }, []);
}

export function GlobalFooter({
  logo,
  newsletter,
  hideNewsletter = false,
  onNewsletterSubmit,
  columns = [],
  socialLinks = [],
  legalText,
  legalLinks = [],
  className,
}: GlobalFooterProps) {
  /* Resolve newsletter: explicit prop > default > hidden */
  const resolvedNewsletter = hideNewsletter ? undefined : (newsletter ?? DEFAULT_NEWSLETTER);
  const [email, setEmail] = React.useState('');
  const [submitState, setSubmitState] = React.useState<SubmitState>('idle');
  const [errorMsg, setErrorMsg] = React.useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitState('pending');
    setErrorMsg('');

    const result = await onNewsletterSubmit?.(email);

    if (result && !result.ok) {
      setSubmitState('error');
      setErrorMsg(result.error);
    } else {
      setSubmitState('success');
      setEmail('');
    }
  };

  return (
    <footer className={cn(styles.root, className)}>
      <div className={styles.container}>
        {/* Newsletter */}
        {resolvedNewsletter && (
          <div className={styles.newsletter}>
            <div className={styles.newsletterCopy}>
              <h2 className={styles.newsletterHeading}>{resolvedNewsletter.heading}</h2>
              <p className={styles.newsletterDesc}>{resolvedNewsletter.description}</p>
            </div>
            <div className={styles.newsletterForm}>
              {submitState === 'success' ? (
                <p className={styles.newsletterDesc} role="status">
                  ✓ You&apos;re subscribed! Thanks for joining.
                </p>
              ) : (
                <form onSubmit={(e) => { void handleSubmit(e); }} className={styles.form}>
                  <Input
                    id="global-footer-email"
                    type="email"
                    placeholder={resolvedNewsletter.inputPlaceholder ?? 'Enter your email'}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-label="Email address for newsletter"
                    aria-describedby={submitState === 'error' ? 'newsletter-error' : undefined}
                    required
                    disabled={submitState === 'pending'}
                  />
                  <Button type="submit" variant="primary" size="sm" disabled={submitState === 'pending'}>
                    {submitState === 'pending' ? 'Subscribing…' : resolvedNewsletter.buttonLabel}
                  </Button>
                </form>
              )}
              {submitState === 'error' && (
                <p id="newsletter-error" className={styles.newsletterDescError} role="alert">
                  {errorMsg}
                </p>
              )}
              {resolvedNewsletter.termsNode && (
                <div className={styles.terms}>{resolvedNewsletter.termsNode}</div>
              )}
            </div>
          </div>
        )}

        {/* Separator between newsletter and columns — matches the bottom divider */}
        <Separator />

        {/* Logo + Link Columns */}
        <div className={styles.columns}>
          <a href={logo.href ?? '/'} className={styles.footerLogo} aria-label="Go to homepage">
            <img src={logo.src} alt={logo.alt ?? ''} className={styles.footerLogoImg} />
          </a>
          {columns.map((col, i) => (
            <div key={i} className={styles.column}>
              <h3 className={styles.columnTitle}>{col.title}</h3>
              <ul className={styles.columnList}>
                {col.links.map((link, j) => (
                  <li key={j}>
                    <a href={link.url} className={styles.columnLink}>
                      {link.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <Separator />

        {/* Legal Row */}
        <div className={styles.legal}>
          <div className={styles.legalLeft}>
            {legalText && (
              <p className={styles.legalText}>{formatLegalText(legalText)}</p>
            )}
            {legalLinks.length > 0 && (
              <nav aria-label="Legal links" className={styles.legalLinks}>
                {legalLinks.map((link, i) => (
                  <a key={i} href={link.url} className={styles.legalLink}>
                    {link.title}
                  </a>
                ))}
              </nav>
            )}
          </div>

          {socialLinks.length > 0 && (
            <div className={styles.social} aria-label="Social media links">
              {socialLinks.map((link, i) => (
                <a
                  key={i}
                  href={link.href}
                  className={styles.socialLink}
                  aria-label={link.label}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.icon}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
}

GlobalFooter.displayName = 'GlobalFooter';
